// MIDAS V3 — Date-group section (Phase 5)
//
// Renders a period group on the history page:
//   • Eyebrow row: small-caps "Tento týždeň · 6 chatov" + right date range.
//   • Thin hairline border under eyebrow.
//   • Row list (caller passes children as <MidasHistoryRowV3 /> instances).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Pure presentational.
import React from "react";

import { Thread } from "../../../midasPageModels";
import { V3DateBucket } from "../v3-utils/threadBuckets";
import MidasHistoryRowV3 from "./MidasHistoryRowV3";

interface MidasHistoryDateGroupV3Props {
  bucket: V3DateBucket;
  threads: Thread[];
  /** Localised label for this bucket (e.g. "Tento týždeň"). */
  label: string;
  /** Localised "{N} chats" label (i18next plural key), e.g. "3 chaty" / "3 chats". */
  formatCount: (count: number) => string;
  /** Right-side date range / hint, already localised by caller. */
  dateHint: string;
  /** machine_id → display name map (from customerMachinery). */
  machineNameById: Record<string, string>;
  /** Currently-loaded thread id (or null) — drives active row marker. */
  activeThreadId: string | null;
  /** Click handler — opens the thread. */
  onOpenThread: (threadId: string) => void;
  /** Localised "PLATFORM" label for the tag pill platform variant. */
  platformLabel: string;
  /** Localised "msgs" suffix for the message-count sub-line. */
  msgsLabel: string;
}

const MidasHistoryDateGroupV3: React.FC<MidasHistoryDateGroupV3Props> = ({
  bucket,
  threads,
  label,
  formatCount,
  dateHint,
  machineNameById,
  activeThreadId,
  onOpenThread,
  platformLabel,
  msgsLabel,
}) => {
  if (!threads || threads.length === 0) return null;

  return (
    <section style={{ marginBottom: 28 }} aria-label={label}>
      {/* Eyebrow row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 18px 10px",
          borderBottom: "1px solid var(--midas-v3-border-soft)",
          marginBottom: 4,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="midas-v3-eyebrow">{label}</span>
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: "var(--midas-v3-faint)",
            }}
          >
            · {formatCount(threads.length)}
          </span>
        </div>
        {dateHint && (
          <span
            className="mono"
            style={{ fontSize: 11, color: "var(--midas-v3-faint)" }}
          >
            {dateHint}
          </span>
        )}
      </div>

      {/* Row list */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {threads.map((t, i) => {
          const label = t.machine_id
            ? machineNameById[t.machine_id] || null
            : null;
          return (
            <MidasHistoryRowV3
              key={`${bucket}-${t.id}`}
              thread={t}
              machineLabel={label}
              activeThreadId={activeThreadId}
              onOpen={onOpenThread}
              platformLabel={platformLabel}
              msgsLabel={msgsLabel}
              index={i}
            />
          );
        })}
      </div>
    </section>
  );
};

export default MidasHistoryDateGroupV3;
